/**
 * ManualFactorEditorMolecule.tsx
 *
 * Editor component for configuring manual check-in status factors.
 * Supports ad-hoc check-ins or scheduled recurring check-ins with
 * configurable comment requirements.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { SelectButton, SelectButtonChangeEvent } from 'primereact/selectbutton';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import DropdownAtom, { type DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import PrimeReactSingleSelect, {
  type OptionType,
} from '../../atoms/single-select-dropdown/single-select-dropdown';
import type { StatusValue } from 'types/spherePoints';
import CheckInScheduleEditorMolecule, {
  type CheckInScheduleValue,
  type AvailablePerspective,
  type SchedulePresetKey,
} from '../CheckInScheduleEditor/CheckInScheduleEditorMolecule';
import type { FactorFormState } from '../../../types/statusFactors';
import type { CheckInTriggerType } from '../CheckInScheduleEditor/CheckInScheduleEditorMolecule';
import './ManualFactorEditorMolecule.scss';

export interface ManualFactorEditorMoleculeProps {
  /** Current form state */
  formState: FactorFormState;
  /** Callback when form state changes */
  onChange: (updates: Partial<FactorFormState>) => void;
  /** Organization ID for timeframe selection */
  /** Sphere ID for context */
  sphereId?: string;
  /** Available perspectives from the schema */
  availablePerspectives?: AvailablePerspective[];
  /** Validation errors keyed by field name */
  validationErrors?: Record<string, string>;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Additional CSS class names */
  className?: string;
}

const ManualFactorEditorMolecule: React.FC<ManualFactorEditorMoleculeProps> = ({
  formState,
  onChange,
  sphereId,
  availablePerspectives = [],
  validationErrors = {},
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();

  /*
   * EAINT-893: DOM element the child (CheckInScheduleEditorMolecule) portals
   * its CADENCE body into. We render an empty slot div inside the CADENCE
   * `<section>` and hand its element down; the child then renders WHO CHECKS
   * IN / REMINDERS / LATE / Advanced / BACKFILL as siblings of the section
   * per the handoff — not children of it, which is the visual bug that made
   * WHO CHECKS IN look nested inside CADENCE.
   *
   * `useState` (not `useRef`) so the second render — after the slot's div
   * mounts — actually flows the element down to the child. A ref would give
   * us the element but not the re-render, so the child's first paint would
   * still see `null` and render CADENCE inline, then flip on the next paint.
   */
  const [cadenceSlot, setCadenceSlot] = useState<HTMLDivElement | null>(null);

  /*
   * Initial status options — the six canonical StatusValue buckets, in
   * the order the user is likely to want them. 'unknown' is first so
   * the safest choice is at the top of the dropdown. See EAINT-748.
   *
   * EAINT-923 (Miro feedback): each option renders with a coloured
   * status dot before its label. The dropdown atom (`PrimeReactSingleSelect`)
   * uses `{ name, code }` — not `{ label, value }` — so keys are mapped
   * here; templates below hang the dot on each row via CSS.
   */
  const initialStatusOptions: OptionType[] = useMemo(
    () => [
      { code: 'unknown', name: t('statusVocabulary.unknown', 'Unknown (no reading)') },
      { code: 'on_track', name: t('statusVocabulary.on_track', 'On track') },
      { code: 'at_risk', name: t('statusVocabulary.at_risk', 'At risk') },
      { code: 'needs_attention', name: t('statusVocabulary.needs_attention', 'Needs attention') },
      { code: 'critical', name: t('statusVocabulary.critical', 'Critical') },
      { code: 'blocked', name: t('statusVocabulary.blocked', 'Blocked') },
    ],
    [t],
  );

  /*
   * EAINT-923: templates for the STARTING STATUS dropdown. Value template
   * (closed state) + item template (each option in the open panel) share
   * a common row: a coloured status dot (via `<i>` styled by
   * `manual-factor-editor__status-dot--<code>` in the SCSS) followed by
   * the status label. Placeholder handled explicitly for empty state.
   */
  const startingStatusValueTemplate = (option: OptionType | null) => {
    if (!option) {
      return (
        <span className="manual-factor-editor__starting-placeholder">
          {t('statusFactors.manual.fields.initialStatusPlaceholder', 'Select a status')}
        </span>
      );
    }
    return (
      <span className="manual-factor-editor__starting-option">
        <i
          className={`manual-factor-editor__status-dot manual-factor-editor__status-dot--${option.code}`}
          aria-hidden="true"
        />
        <span>{option.name}</span>
      </span>
    );
  };

  const startingStatusItemTemplate = (option: OptionType) => (
    <span className="manual-factor-editor__starting-option">
      <i
        className={`manual-factor-editor__status-dot manual-factor-editor__status-dot--${option.code}`}
        aria-hidden="true"
      />
      <span>{option.name}</span>
    </span>
  );

  // Check-in mode options: ad-hoc or scheduled
  const checkInModeOptions = useMemo(
    () => [
      {
        value: 'adhoc' as CheckInTriggerType,
        label: t('statusFactors.manual.mode.adhoc', 'Ad-hoc'),
        icon: 'pi pi-user-edit',
      },
      {
        value: 'recurring' as CheckInTriggerType,
        label: t('statusFactors.manual.mode.scheduled', 'Scheduled'),
        icon: 'pi pi-calendar-times',
      },
    ],
    [t]
  );

  // Handle check-in mode change
  const handleModeChange = (e: SelectButtonChangeEvent) => {
    const newTrigger = e.value as CheckInTriggerType;
    onChange({
      checkInTrigger: newTrigger,
      checkInSchedule: {
        ...formState.checkInSchedule,
        triggerType: newTrigger,
      },
    });
  };

  // Handle schedule configuration change
  const handleScheduleChange = (scheduleValue: CheckInScheduleValue) => {
    onChange({
      checkInSchedule: scheduleValue,
    });
  };

  // Handle template selection change
  const handleTemplateChange = (template: SchedulePresetKey | null) => {
    onChange({
      selectedScheduleTemplate: template,
    });
  };

  // Handle comment required toggle
  const handleCommentRequiredChange = (e: CheckboxChangeEvent) => {
    onChange({
      commentRequired: e.checked || false,
      // Reset min length when disabling requirement
      minCommentLength: e.checked ? formState.minCommentLength || 10 : 0,
    });
  };

  // Handle min comment length change
  const handleMinCommentLengthChange = (e: InputNumberValueChangeEvent) => {
    onChange({
      minCommentLength: e.value || 0,
    });
  };

  const isScheduled = formState.checkInTrigger !== 'adhoc';
  const classes = ['manual-factor-editor', className].filter(Boolean).join(' ');

  // Board 3 pairs STARTING STATUS with REMINDERS & ESCALATION in a two-column
  // grid. When scheduled we hand this to CheckInScheduleEditor via its
  // startingStatusSlot prop so both cards render on the same row; when
  // ad-hoc there's no REMINDERS card, and STARTING STATUS renders standalone.
  const startingStatusCard = (
    <section className="manual-factor-editor__card manual-factor-editor__card--starting-status">
      <div className="manual-factor-editor__card-title">
        {t('statusFactors.manual.sections.startingStatus', 'STARTING STATUS')}
      </div>
      <div className="manual-factor-editor__starting-body">
        {/* Board 3: single row [label] ...... [select]. Raw <select> so it
            reads like the reminders inputs — same crisp bordered-rect chrome. */}
        {/* EAINT-923: swapped from a raw <select> to PrimeReactSingleSelect
            so each option can render a coloured status dot alongside its
            label. `panelClassName="manual-factor-editor-starting-panel"`
            scopes the portalled panel's styling to this consumer only
            (see the EAINT-893 opt-in pattern the atom exposes). */}
        <div className="manual-factor-editor__starting-row">
          <label htmlFor="manual-initial-status">
            {t('statusFactors.manual.fields.initialStatus', 'Status before first check-in')}
          </label>
          <PrimeReactSingleSelect
            value={
              initialStatusOptions.find(
                (o) => o.code === (formState.initialStatus ?? 'unknown'),
              ) ?? null
            }
            options={initialStatusOptions}
            onChange={(e) => {
              const next = (e.value?.code ?? 'unknown') as StatusValue;
              onChange({ initialStatus: next });
            }}
            valueTemplate={startingStatusValueTemplate}
            itemTemplate={startingStatusItemTemplate}
            className="manual-factor-editor__starting-select"
            panelClassName="manual-factor-editor-starting-panel"
            disabled={disabled}
            data-testid="starting-status-select"
          />
        </div>

        {/* Ad-hoc has no CheckInScheduleEditor → no WHO CHECKS IN card,
            so the comment settings live in STARTING STATUS. In Scheduled
            mode they render inside WHO CHECKS IN. Same inline toggle +
            raw-input layout as the Scheduled path. */}
        {!isScheduled && (
          <div className="manual-factor-editor__comment-block">
            <div className="manual-factor-editor__toggle-row">
              <label
                htmlFor="commentRequired"
                className="manual-factor-editor__toggle-label"
              >
                {t('statusFactors.manual.fields.commentRequired', 'Require a comment')}
              </label>
              <SwitchAtom
                inputId="commentRequired"
                checked={formState.commentRequired}
                onChange={(next) =>
                  onChange({
                    commentRequired: next,
                    minCommentLength: next ? formState.minCommentLength || 10 : 0,
                  })
                }
                disabled={disabled}
              />
            </div>
            {formState.commentRequired && (
              <div className="manual-factor-editor__min-length-row">
                <label htmlFor="minCommentLength">
                  {t('statusFactors.manual.fields.minCommentLength', 'Min length')}
                </label>
                <div className="manual-factor-editor__min-length-right">
                  <input
                    id="minCommentLength"
                    type="number"
                    className="manual-factor-editor__min-length-input"
                    value={formState.minCommentLength ?? 0}
                    min={0}
                    max={500}
                    onChange={(e) =>
                      onChange({ minCommentLength: Number(e.target.value) || 0 })
                    }
                    disabled={disabled}
                  />
                  <span className="manual-factor-editor__min-length-unit">
                    {t('statusFactors.manual.fields.chars', 'chars')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className={classes}>
      {/* Board 3 layout below the identity header:
           - CADENCE card (Ad-hoc/Scheduled + schedule config)
           - WHO CHECKS IN card (responsibility + comment settings)
           - STARTING STATUS + REMINDERS & ESCALATION cards side-by-side
           - Advanced options disclosure
           - CREATE PERIODS FOR PAST DATES ON DEPLOY (backfill warning)
           CheckInScheduleEditor renders the CADENCE / WHO CHECKS IN /
           REMINDERS / Advanced / backfill cards. This component only
           owns the Ad-hoc/Scheduled toggle (which lives at the top of
           CADENCE) and the STARTING STATUS card. */}
      <section className="manual-factor-editor__card manual-factor-editor__card--cadence">
        <div className="manual-factor-editor__card-title">
          {t('statusFactors.manual.sections.cadence', 'CADENCE — WHEN IS A CHECK-IN DUE')}
        </div>
        <div className="manual-factor-editor__cadence-body">
          <SelectButton
            value={formState.checkInTrigger || 'adhoc'}
            onChange={handleModeChange}
            options={checkInModeOptions}
            optionLabel="label"
            optionValue="value"
            disabled={disabled}
            className="manual-factor-editor__mode-selector"
          />
          <div className="manual-factor-editor__mode-description">
            {formState.checkInTrigger === 'adhoc' ? (
              <p>
                <i className="pi pi-info-circle" />
                {t(
                  'statusFactors.manual.hints.adhoc',
                  'Users can submit status updates at any time. No scheduled reminders or deadlines.'
                )}
              </p>
            ) : (
              <p>
                <i className="pi pi-info-circle" />
                {t(
                  'statusFactors.manual.hints.scheduled',
                  'Status updates are due on a recurring schedule. Users receive reminders and overdue notifications.'
                )}
              </p>
            )}
          </div>
          {/*
           * EAINT-893: Slot for CheckInScheduleEditor's CADENCE body. Empty
           * on the first render pass; on the second (after this ref runs),
           * the child portals its CADENCE body into it. See the useState
           * above the return for the "why useState not useRef" note.
           */}
          {isScheduled && (
            <div
              ref={setCadenceSlot}
              className="manual-factor-editor__cadence-slot"
              data-testid="cadence-slot"
            />
          )}
          {!isScheduled && validationErrors.schedule && (
            <small className="form-field__error">{validationErrors.schedule}</small>
          )}
        </div>
      </section>

      {/*
       * EAINT-893: CheckInScheduleEditor renders AS A SIBLING of the CADENCE
       * section, not inside it. Its CADENCE body is portalled into
       * `cadenceSlot` (mounted inside the section above); WHO CHECKS IN /
       * REMINDERS / LATE / Advanced / BACKFILL render here as siblings so
       * the visual card boundaries match the handoff.
       */}
      {isScheduled && (
        <CheckInScheduleEditorMolecule
          value={formState.checkInSchedule}
          onChange={handleScheduleChange}
          selectedTemplate={formState.selectedScheduleTemplate as SchedulePresetKey | null}
          onTemplateChange={handleTemplateChange}
          sphereId={sphereId}
          availablePerspectives={availablePerspectives}
          readOnly={disabled}
          commentRequired={formState.commentRequired}
          minCommentLength={formState.minCommentLength}
          onCommentRequiredChange={(next) =>
            onChange({
              commentRequired: next,
              minCommentLength: next ? formState.minCommentLength || 10 : 0,
            })
          }
          onMinCommentLengthChange={(next) =>
            onChange({ minCommentLength: next || 0 })
          }
          startingStatusSlot={startingStatusCard}
          cadenceSlot={cadenceSlot}
          className="manual-factor-editor__schedule-editor"
        />
      )}

      {/* Ad-hoc mode: STARTING STATUS renders standalone (no REMINDERS pair).
          Scheduled mode injects it into CheckInScheduleEditor's slot above. */}
      {!isScheduled && startingStatusCard}
    </div>
  );
};

ManualFactorEditorMolecule.displayName = 'ManualFactorEditorMolecule';

export default ManualFactorEditorMolecule;
